/* eslint-disable */

// The MIT License (MIT)
//
// Copyright (c) 2014, Gerger (www.gerger.co)
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

/*
 * @class DecimalFormat
 * @author Oskan Savli
 */
export class DecimalFormat {
  /** @param {String} formatStr */
  constructor (formatStr) {
    /**
     * @fieldOf DecimalFormat
     * @type String
     */
    this.prefix = '';
    /**
     * @fieldOf DecimalFormat
     * @type String
     */
    this.suffix = '';
    /**
     * @description Grouping size
     * @fieldOf DecimalFormat
     * @type String
     */
    this.comma = 0;
    /**
     * @description Minimum integer digits to be displayed
     * @fieldOf DecimalFormat
     * @type Number
     */
    this.minInt = 1;
    /**
     * @description Minimum fractional digits to be displayed
     * @fieldOf DecimalFormat
     * @type String
     */
    this.minFrac = 0;
    /**
     * @description Maximum fractional digits to be displayed
     * @fieldOf DecimalFormat
     * @type String
     */
    this.maxFrac = 0;

    // get prefix
    for (let i = 0; i < formatStr.length; i++) {
      if (formatStr.charAt(i) === '#' || formatStr.charAt(i) === '0') {
        this.prefix = formatStr.substring(0, i);
        formatStr = formatStr.substring(i);
        break;
      }
    }

    // get suffix
    this.suffix = formatStr.replace(/[#]|[0]|[,]|[.]/g, '');

    // get number as string
    const numberStr = formatStr.replace(/[^0#,.]/g, '');

    let intStr = '';
    let fracStr = '';
    const point = numberStr.indexOf('.');
    if (point !== -1) {
      intStr = numberStr.substring(0, point);
      fracStr = numberStr.substring(point + 1);
    } else {
      intStr = numberStr;
    }

    const commaPos = intStr.lastIndexOf(',');
    if (commaPos !== -1) {
      this.comma = intStr.length - 1 - commaPos;
    }

    intStr = intStr.replace(/[,]/g, ''); // remove commas

    fracStr = fracStr.replace(/[,]|[.]+/g, '');

    this.maxFrac = fracStr.length;
    let tmp = intStr.replace(/[^0]/g, ''); // remove all except zero
    if (tmp.length > this.minInt) {
      this.minInt = tmp.length;
    }
    tmp = fracStr.replace(/[^0]/g, '');
    this.minFrac = tmp.length;
  }

  /**
   * Formats given value
   *
   * @methodOf DecimalFormat
   * @param {number|string} numStr represents the number as string to format
   * @return {string} Formatted number
   * @author Oskan Savli
   */
  format (numStr) { // 1223.06 --> $1,223.06
    // remove prefix, suffix and commas
    let numberStr = this.formatBack(numStr).toLowerCase();

    // do not format if not a number
    if (isNaN(numberStr) || numberStr.length === 0) {
      return numStr;
    }

    // scientific numbers
    const i = numberStr.indexOf('e') !== -1;
    if (i) {
      const n = Number(numberStr);
      if (n === 'Infinity' || n === '-Infinity') {
        return numberStr;
      }
      numberStr = n + '';
      if (numberStr.indexOf('e') !== -1) {
        return numberStr;
      }
    }

    let negative = false;
    // remove sign
    if (numberStr.charAt(0) === '-') {
      negative = true;
      numberStr = numberStr.substring(1);
    } else if (numberStr.charAt(0) === '+') {
      numberStr = numberStr.substring(1);
    }

    const point = numberStr.indexOf('.'); // position of point character
    let intStr = '';
    let fracStr = '';
    if (point !== -1) {
      intStr = numberStr.substring(0, point);
      fracStr = numberStr.substring(point + 1);
    } else {
      intStr = numberStr;
    }
    fracStr = fracStr.replace(/[.]/, ''); // remove other point characters

    const isPercentage = this.suffix && this.suffix.charAt(0) === '%';
    // if percentage, number will be multiplied by 100.
    let minInt = this.minInt;
    let minFrac = this.minFrac;
    let maxFrac = this.maxFrac;
    if (isPercentage) {
      minInt -= 2;
      minFrac += 2;
      maxFrac += 2;
    }

    if (fracStr.length > maxFrac) { // round
      // case 6143
      let num = Number('0.' + fracStr);
      num = (maxFrac === 0) ? Math.round(num) : num.toFixed(maxFrac);
      // toFixed method has bugs on IE (0.7 --> 0)
      fracStr = num.toString(10).substr(2);
      let c = (num >= 1) ? 1 : 0; // carry
      let x;
      let i = intStr.length - 1;
      while (c) { // increment intStr
        if (i === -1) {
          intStr = '1' + intStr;
          break;
        } else {
          x = intStr.charAt(i);
          if (x === 9) {
            x = '0';
            c = 1;
          } else {
            x = (++x) + '';
            c = 0;
          }
          intStr = intStr.substring(0, i) + x + intStr.substring(i + 1, intStr.length);
          i--;
        }
      }
    }
    for (let i = fracStr.length; i < minFrac; i++) { // if minFrac=4 then 1.12 --> 1.1200
      fracStr = fracStr + '0';
    }
    while (fracStr.length > minFrac && fracStr.charAt(fracStr.length - 1) === '0') { // if minInt=4 then 00034 --> 0034)
      fracStr = fracStr.substring(0, fracStr.length - 1);
    }

    for (let i = intStr.length; i < minInt; i++) { // if minInt=4 then 034 --> 0034
      intStr = '0' + intStr;
    }
    while (intStr.length > minInt && intStr.charAt(0) === '0') { // if minInt=4 then 00034 --> 0034)
      intStr = intStr.substring(1);
    }

    if (isPercentage) { // multiply by 100
      intStr += fracStr.substring(0, 2);
      fracStr = fracStr.substring(2);
    }

    let j = 0;
    for (let i = intStr.length; i > 0; i--) { // add commas
      if (j !== 0 && j % this.comma === 0) {
        intStr = intStr.substring(0, i) + ',' + intStr.substring(i);
        j = 0;
      }
      j++;
    }

    let formattedValue;
    if (fracStr.length > 0) {
      formattedValue = this.prefix + intStr + '.' + fracStr + this.suffix;
    } else {
      formattedValue = this.prefix + intStr + this.suffix;
    }

    if (negative) {
      formattedValue = '-' + formattedValue;
    }

    return formattedValue;
  }

  /**
   * Converts formatted value back to non-formatted value
   *
   * @protected
   * @methodOf DecimalFormat
   * @param {String} fNumStr Formatted number
   * @return {String} Original number
   * @author Oskan Savli
   */
  formatBack (fNumStr) { // $1,223.06 --> 1223.06
    fNumStr += ''; // ensure it is string
    if (!fNumStr) {
      return '';
    } // do not return undefined or null
    if (!isNaN(fNumStr)) {
      return this.getNumericString(fNumStr);
    }
    let fNumberStr = fNumStr;
    let negative = false;
    if (fNumStr.charAt(0) === '-') {
      fNumberStr = fNumberStr.substr(1);
      negative = true;
    }
    const pIndex = fNumberStr.indexOf(this.prefix);
    const sIndex = (this.suffix === '') ? fNumberStr.length : fNumberStr.indexOf(this.suffix, this.prefix.length + 1);
    if (pIndex === 0 && sIndex > 0) {
      // remove suffix
      fNumberStr = fNumberStr.substr(0, sIndex);
      // remove prefix
      fNumberStr = fNumberStr.substr(this.prefix.length);
      // remove commas
      fNumberStr = fNumberStr.replace(/,/g, '');
      if (negative) {
        fNumberStr = '-' + fNumberStr;
      }
      if (!isNaN(fNumberStr)) {
        return this.getNumericString(fNumberStr);
      }
    }
    return fNumStr;
  }

  /*
   * @description We shouldn't return strings like 1.000 in formatBack method.
   * However, using only Number(str) is not enough, because it omits . in big numbers
   * like 23423423423342234.34 => 23423423423342236 . There's a conflict in cases
   * 6143 and 6541.
   * @methodOf DecimalFormat
   * @param {String} str Numberic string
   * @return {String} Corrected numeric string
   * @author Serdar Bicer
   */
  getNumericString (str) {
    // first convert to number
    const num = Number(str);
    // check if there is a missing dot
    const numStr = num + '';
    if (str.indexOf('.') > -1 && numStr.indexOf('.') < 0) {
      // check if original string has all zeros after dot or not
      for (let i = str.indexOf('.') + 1; i < str.length; i++) {
        // if not, this means we lost precision
        if (str.charAt(i) !== '0') {
          return str;
        }
      }
      return numStr;
    }
    return str;
  };
}
